import { captureException, startSpan } from "@sentry/react";

import {
    GameEntityDTO,
    ICreateMonsterStatsRequest,
    ICreateNPCRequest,
    IMonsterStatsResponse,
    INPCStatsResponse,
} from "../../types";
import { doDelete, doGet, doPostJson } from "../core/http";

// Monster Stats
export const getMonsterStatsByImageId = async (
    imageId: string
): Promise<GameEntityDTO | null> => {
    const url = `/api/stats/monsters/${imageId}`;
    try {
        const response = await startSpan(
            { name: "getMonsterStatsByImageId" },
            async () => {
                return doGet(url, false);
            }
        );
        if (response.error) {
            if (response.error.status === 404) {
                return null;
            } else {
                captureException(response.error);
                return null;
            }
        }
        return response.data as GameEntityDTO;
    } catch (e) {
        captureException(e);
        return null;
    }
};

export const createMonsterStats = async (
    statsRequest: ICreateMonsterStatsRequest
): Promise<IMonsterStatsResponse> => {
    try {
        const response = await startSpan(
            { name: "createMonsterStats" },
            async () => {
                return doPostJson("/api/stats/monsters", true, statsRequest);
            }
        );

        if (response.error) {
            captureException(response.error);
            return { error: response.error };
        }

        return { taskId: response.data };
    } catch (e) {
        captureException(e);
        return { error: "Failed to create monster stats." };
    }
};

export const getStatus = async (taskId: string): Promise<boolean | null> => {
    // NOTE: This function was originally ambiguous (used for both monster and NPC status).
    // Assuming it polls the MONSTER status endpoint here.
    // A separate function might be needed for NPC status polling if the endpoint differs.
    const url = `/api/stats/monsters/${taskId}/status`;
    try {
        const response = await startSpan(
            { name: "getMonsterStatus" }, // More specific name
            async () => {
                return doGet(url, true);
            }
        );
        if (response.error) {
            if (response.error.status === 404) {
                return null;
            } else {
                captureException(response.error);
                return null;
            }
        }
        return response.data;
    } catch (e) {
        captureException(e);
        return null;
    }
};

// NPC Stats
export const getNPCStatsByImageId = async (
    imageId: string
): Promise<GameEntityDTO | null> => {
    const url = `/api/stats/npcs/${imageId}`;
    try {
        const response = await startSpan(
            { name: "getNPCStatsByImageId" },
            async () => {
                return doGet(url, false);
            }
        );
        if (response.error) {
            if (response.error.status === 404) {
                return null;
            } else {
                captureException(response.error);
                return null;
            }
        }
        return response.data as GameEntityDTO;
    } catch (e) {
        captureException(e);
        return null;
    }
};

export const createNpcStats = async (
    statsRequest: ICreateNPCRequest
): Promise<INPCStatsResponse> => {
    try {
        const response = await startSpan(
            { name: "createNPCStats" },
            async () => {
                return doPostJson("/api/stats/npcs", true, statsRequest);
            }
        );

        if (response.error) {
            captureException(response.error);
            return { error: response.error };
        }

        return { taskId: response.data };
    } catch (e) {
        captureException(e);
        return { error: "Failed to create NPC stats." }; // Corrected error message
    }
};

export const getNpcStatus = async (taskId: string): Promise<boolean | null> => {
    const url = `/api/stats/npcs/${taskId}/status`;
    try {
        const response = await startSpan(
            { name: "getNpcStatus" }, // More specific name
            async () => {
                return doGet(url, true);
            }
        );
        if (response.error) {
            if (response.error.status === 404) {
                return null;
            } else {
                captureException(response.error);
                return null;
            }
        }
        return response.data;
    } catch (e) {
        captureException(e);
        return null;
    }
};

// Generic Entity Deletion
export const deleteEntityById = async (
    entityType: string,
    id: string
): Promise<void> => {
    const url = `/api/stats/entities/${entityType}/${id}`;
    try {
        const response = await startSpan(
            { name: "deleteEntityById" },
            async () => {
                return doDelete(url, true);
            }
        );
        if (response.error) {
            throw new Error(response.error);
        }
    } catch (e) {
        captureException(e);
        throw new Error(`Failed to delete ${entityType} stats.`);
    }
};
